import { useEffect, useState } from 'react';
import { LEVELS_BY_KEY } from './courseLevels';
import { sortByPosition } from './ordering';
import { TopicIcon } from './OutlineNode';
import * as api from './api';

// Small stacked-books glyph for the new course-root row below - same
// hand-built-SVG convention as TopicIcon (viewBox 24x24, stroke-only, only
// line/polygon primitives). PreviewBreadcrumb.jsx deliberately has no
// CourseIcon ("Course itself is never rendered" there, per its own explicit
// user-driven design) - that's a separate concern from this nav list, which
// now needs a real click target for "preview the whole course in one
// scroll" (there was none before).
export function CourseIcon() {
   return (
      <svg viewBox='0 0 24 24' width='13' height='13' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
         <polygon points='4,3 14,3 14,20 4,20' />
         <polygon points='14,5 20,5 20,22 14,22' />
         <line x1='7' y1='8' x2='11' y2='8' />
         <line x1='7' y1='12' x2='11' y2='12' />
      </svg>
   );
}

// The Preview tab's nav used to be the exact same nested/expandable tree as
// the Constructor, just restyled - a real table of contents doesn't read
// that way, so this is a flat, non-nested walk instead. First cut also
// interleaved plain gray Module/Chapter/Unit/Lesson section markers between
// groups of topics, but the user pointed out PreviewBreadcrumb already
// shows that same hierarchy (redesigned alongside this change to actually
// label it well) - repeating it a second time in the nav was redundant, so
// this only lists Topics now, one after another, since Topics are what
// actually fills the course ("merytoryczne dla nawigacji" - the structural
// levels are organizational, not content - belongs in the breadcrumb, not
// competing for attention in the nav list).
//
// Depth-first, position-ordered walk: at every level, that level's own
// directly-attached Topics come first (same "own content, then further
// structure" order the constructor tree already uses), then recurse into
// each structural child. Mirrors collectTopicsUnder in
// preview/TopicPreviewPanel.jsx (same direct api.getList calls, not the
// constructor's expand/collapse cache - there's nothing to expand here) but
// also keeps hostLevelKey/hostId per topic, which that function has no
// reason to since it only needs the topic objects themselves.
async function collectFlatTopics(levelKey, id) {
   const level = LEVELS_BY_KEY[levelKey];
   let items = [];

   if (level.canHostTopics) {
      const topicResponse = await api.getList(LEVELS_BY_KEY.topic.apiBase, level.key, id);
      const topics = sortByPosition(Array.isArray(topicResponse.data) ? topicResponse.data : []);
      items = items.concat(topics.map((topic) => ({ topic, hostLevelKey: level.key, hostId: id })));
   }

   if (level.childKey) {
      const childLevel = LEVELS_BY_KEY[level.childKey];
      const childResponse = await api.getList(childLevel.apiBase, childLevel.filterParam, id);
      const children = sortByPosition(Array.isArray(childResponse.data) ? childResponse.data : []);
      for (const child of children) {
         const childItems = await collectFlatTopics(childLevel.key, child.id);
         items = items.concat(childItems);
      }
   }

   return items;
}

// `activeTopicId` (new) is purely a "where's my scroll position right now"
// marker - independent of `actions.isSelected`, which only ever reflects
// what was last CLICKED (a Module, the Course root, a single Topic...).
// Clicking the Course row flattens every topic in the whole course into one
// scroll (TopicPreviewPanel.jsx already does this correctly for ANY
// hostable levelKey, 'course' included - collectTopicsUnder was already
// generic, it just had no click target before) - `selected` then stays
// fixed on {levelKey:'course'}, so none of the individual topic rows below
// would ever show `.selected` while scrolling through them. activeTopicId
// is the only indicator of "which topic am I actually looking at right
// now" in that (very common) case, tracked via IntersectionObserver in
// TopicPreviewPanel.jsx and threaded down through Workspace.jsx/OutlineTree.
export default function PreviewNavList({ courseId, courseName, actions, activeTopicId }) {
   const [state, setState] = useState({ loading: true, items: [] });

   useEffect(() => {
      let cancelled = false;
      setState({ loading: true, items: [] });
      collectFlatTopics('course', courseId).then((items) => {
         if (!cancelled) setState({ loading: false, items });
      });
      return () => { cancelled = true; };
   }, [courseId]);

   return (
      <div className='course-tree-flat-list'>
         <div className={'course-tree-row course-tree-row-course' + (actions.isSelected('course', courseId) ? ' selected' : '')}
            onClick={() => actions.select('course', courseId)}>
            <span className='course-tree-topic-icon' title='Курс'><CourseIcon /></span>
            <span className='course-tree-node-label'>{courseName || 'Увесь курс'}</span>
         </div>
         {state.loading ? (
            <div className='course-tree-loading'>...</div>
         ) : state.items.length === 0 ? (
            <div className='course-tree-flat-empty'>Курс ще не має тем.</div>
         ) : (
            state.items.map(({ topic, hostLevelKey, hostId }) => (
               <div key={'topic-' + topic.id}
                  className={'course-tree-row course-tree-row-topic' + (actions.isSelected('topic', topic.id) ? ' selected' : '')
                     + (activeTopicId === topic.id ? ' in-view' : '')}
                  onClick={() => actions.select('topic', topic.id, hostId, hostLevelKey)}>
                  {activeTopicId === topic.id ? <span className='course-tree-in-view-dot' /> : ''}
                  <span className='course-tree-topic-icon' title='Тема'><TopicIcon /></span>
                  <span className='course-tree-node-label'>{topic.name}</span>
               </div>
            ))
         )}
      </div>
   );
}
